"use client";

import {
  faCircleQuestion,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isNil } from "lodash";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";

import ContinuousAreaChart, {
  ContinuousAreaGraphInput,
} from "@/components/charts/continuous_area_chart";
import SectionToggle from "@/components/ui/section_toggle";
import Switch from "@/components/ui/switch";
import Tooltip from "@/components/ui/tooltip";
import { ContinuousAreaGraphType } from "@/types/charts";
import {
  DisagreementForecastData,
  QuestionType,
  QuestionWithForecasts,
  QuestionWithNumericForecasts,
  ScoreData,
} from "@/types/question";
import cn from "@/utils/core/cn";
import {
  isContinuousQuestionType,
  isDiscreteQuestion,
} from "@/utils/questions/helpers";

type Props = {
  question: QuestionWithForecasts;
};

type DisagreementScoreData = ScoreData & {
  disagreement_forecasts?: DisagreementForecastData | null;
};

const CHART_WIDTH = 640;
const CHART_HEIGHT = 220;
const PAD_X = 36;
const PAD_Y = 24;

const formatDateTime = (timestamp: number) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp * 1000));

const toFiniteValues = (values: (number | null)[]) =>
  values.map((value) =>
    isNil(value) || !Number.isFinite(value) ? null : value
  );

const buildPath = (values: (number | null)[]) => {
  const finiteValues = toFiniteValues(values);
  const maxIndex = Math.max(finiteValues.length - 1, 1);
  const points = finiteValues
    .map((value, index) => {
      if (isNil(value)) return null;
      const x = PAD_X + (index / maxIndex) * (CHART_WIDTH - PAD_X * 2);
      const y = PAD_Y + (1 - value) * (CHART_HEIGHT - PAD_Y * 2);
      return `${x},${y}`;
    })
    .filter(Boolean);

  return points.length ? `M ${points.join(" L ")}` : "";
};

const getAxisLabels = (question: QuestionWithForecasts) => {
  if (question.type === QuestionType.Binary) {
    return ["No", "Yes"];
  }

  if (question.type === QuestionType.MultipleChoice) {
    return question.options ?? [];
  }

  return [];
};

const isContinuousScoringQuestion = (
  question: QuestionWithForecasts
): question is QuestionWithNumericForecasts =>
  isContinuousQuestionType(question.type);

const toNumberArray = (values: (number | null)[]) => {
  const parsed = values.filter(
    (value): value is number => !isNil(value) && Number.isFinite(value)
  );
  return parsed.length === values.length ? parsed : null;
};

const DisagreementForecastsChart = ({ question }: Props) => {
  const t = useTranslations();
  const isContinuousQuestion = isContinuousScoringQuestion(question);
  const discrete = isDiscreteQuestion(question);
  const scoreData = question.aggregations?.[question.default_aggregation_method]
    ?.score_data as DisagreementScoreData | undefined;
  const disagreementData = scoreData?.disagreement_forecasts;
  const [activeIndex, setActiveIndex] = useState(0);
  const [continuousGraphType, setContinuousGraphType] =
    useState<ContinuousAreaGraphType>("cdf");

  const chartRows = useMemo(() => {
    return (
      disagreementData?.forecasts.map((forecast) => ({
        forecast,
        chartValues: forecast.pmf,
      })) ?? []
    );
  }, [disagreementData?.forecasts]);

  const continuousChartData = useMemo<ContinuousAreaGraphInput>(() => {
    if (!disagreementData || !isContinuousQuestion) return [];

    const activeForecast = disagreementData.forecasts[activeIndex];
    if (!activeForecast) return [];

    const cdf = toNumberArray(activeForecast.values);
    const pmf = toNumberArray(activeForecast.pmf);
    if (!cdf || !pmf) return [];

    return [{ cdf, pmf, type: "user" }];
  }, [activeIndex, disagreementData, isContinuousQuestion]);

  if (!disagreementData || chartRows.length < 2) return null;

  // Keyed on the stored JSD score (not the live jsd_score) to match the
  // additional scores table.
  const showLegacyScore = isNil(scoreData?.disagreement_jsd_score);

  const fallbackRow = chartRows[0];
  if (!fallbackRow) return null;

  const activeForecast =
    chartRows[activeIndex]?.forecast ?? fallbackRow.forecast;
  const axisLabels = getAxisLabels(question);
  const canCycle = chartRows.length > 1;

  const moveActive = (direction: -1 | 1) => {
    setActiveIndex((index) => {
      const next = index + direction;
      if (next < 0) return chartRows.length - 1;
      if (next >= chartRows.length) return 0;
      return next;
    });
  };

  return (
    <SectionToggle title="Disagreement scoring forecasts" defaultOpen>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-start justify-between gap-4 text-sm text-gray-700 dark:text-gray-700-dark">
          <div className="flex gap-6">
            {showLegacyScore && (
              <div>
                <div className="text-xs uppercase text-gray-500 dark:text-gray-500-dark">
                  {t("disagreementScore")}
                </div>
                <div className="text-base text-gray-900 dark:text-gray-900-dark">
                  {!isNil(disagreementData.score)
                    ? disagreementData.score.toFixed(2)
                    : "—"}
                </div>
              </div>
            )}
            <div>
              <div className="text-xs uppercase text-gray-500 dark:text-gray-500-dark">
                {t("disagreementJsdScore")}
              </div>
              <div className="text-base text-gray-900 dark:text-gray-900-dark">
                {!isNil(disagreementData.jsd_score)
                  ? disagreementData.jsd_score.toFixed(2)
                  : "—"}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase text-gray-500 dark:text-gray-500-dark">
              Panel forecasts
            </div>
            <div className="text-base text-gray-900 dark:text-gray-900-dark">
              {disagreementData.forecast_count}
            </div>
            {isContinuousQuestion && (
              <div className="mt-2 flex w-fit flex-row items-center gap-2">
                <p
                  className={cn(
                    "m-0 text-sm",
                    continuousGraphType === "cdf" ? "opacity-60" : "opacity-90"
                  )}
                  title="probability density function"
                >
                  {discrete ? t("pmf") : t("pdf")}
                </p>
                <Switch
                  checked={continuousGraphType === "cdf"}
                  onChange={(checked) =>
                    setContinuousGraphType(checked ? "cdf" : "pmf")
                  }
                />
                <p
                  className={cn(
                    "m-0 text-sm",
                    continuousGraphType === "cdf" ? "opacity-90" : "opacity-60"
                  )}
                  title="cumulative density function"
                >
                  {t("cdf")}
                </p>
                <Tooltip
                  showDelayMs={200}
                  placement="bottom"
                  renderInPortal={false}
                  tooltipContent={
                    (discrete
                      ? "PMF (Probability Mass Function) shows how likely different specific outcomes are,"
                      : "PDF (Probability Density Function) shows how likely different outcomes are around specific values,") +
                    " while CDF (Cumulative Distribution Function) shows the cumulative probability of outcomes up to a certain value."
                  }
                  tooltipClassName="text-center !max-w-[331px] !border-blue-400 dark:!border-blue-400-dark bg-gray-0 dark:bg-gray-0-dark !text-base !p-4"
                >
                  <FontAwesomeIcon
                    icon={faCircleQuestion}
                    height={16}
                    className="text-gray-500 hover:text-blue-800 dark:text-gray-500-dark dark:hover:text-blue-800-dark"
                  />
                </Tooltip>
              </div>
            )}
          </div>
        </div>

        <p className="m-0 text-sm text-gray-700 dark:text-gray-700-dark">
          This chart shows all included forecasts that were active at scoring
          time.
        </p>

        {isContinuousQuestion && continuousChartData.length ? (
          <div className="overflow-hidden rounded border border-gray-300 bg-white dark:border-gray-300-dark dark:bg-gray-0-dark">
            <ContinuousAreaChart
              question={question}
              data={continuousChartData}
              graphType={continuousGraphType}
              height={220}
            />
          </div>
        ) : (
          <div className="overflow-hidden rounded border border-gray-300 bg-white dark:border-gray-300-dark dark:bg-gray-0-dark">
            <svg
              viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
              className="block aspect-[32/11] w-full"
              role="img"
              aria-label="All disagreement scoring forecasts"
            >
              <line
                x1={PAD_X}
                x2={CHART_WIDTH - PAD_X}
                y1={CHART_HEIGHT - PAD_Y}
                y2={CHART_HEIGHT - PAD_Y}
                stroke="currentColor"
                className="text-gray-300 dark:text-gray-300-dark"
              />
              <line
                x1={PAD_X}
                x2={PAD_X}
                y1={PAD_Y}
                y2={CHART_HEIGHT - PAD_Y}
                stroke="currentColor"
                className="text-gray-300 dark:text-gray-300-dark"
              />
              {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
                const y = PAD_Y + (1 - tick) * (CHART_HEIGHT - PAD_Y * 2);
                return (
                  <g key={tick}>
                    <line
                      x1={PAD_X}
                      x2={CHART_WIDTH - PAD_X}
                      y1={y}
                      y2={y}
                      stroke="currentColor"
                      className="text-gray-200 dark:text-gray-200-dark"
                    />
                    <text
                      x={PAD_X - 8}
                      y={y + 4}
                      textAnchor="end"
                      className="fill-gray-500 text-[11px] dark:fill-gray-500-dark"
                    >
                      {tick * 100}
                    </text>
                  </g>
                );
              })}
              {chartRows.map(({ forecast, chartValues }, index) => {
                const active = index === activeIndex;
                const path = buildPath(chartValues);
                if (!path) return null;

                return (
                  <path
                    key={`${forecast.author_id}-${forecast.start_time}`}
                    d={path}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={active ? 4 : 1.6}
                    className={cn(
                      active
                        ? "text-blue-700 dark:text-blue-700-dark"
                        : "text-gray-500 dark:text-gray-500-dark"
                    )}
                    opacity={active ? 1 : 0.35}
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
            </svg>
            <div className="flex justify-between gap-3 px-4 pb-3 text-xs text-gray-500 dark:text-gray-500-dark">
              <span>{axisLabels[0]}</span>
              <span>{axisLabels[Math.floor(axisLabels.length / 2)]}</span>
              <span>{axisLabels[axisLabels.length - 1]}</span>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3 rounded border border-gray-300 p-3 dark:border-gray-300-dark sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-900-dark">
              {activeForecast.author_username}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-600-dark">
              Forecast {activeIndex + 1} of {chartRows.length} - started{" "}
              {formatDateTime(activeForecast.start_time)}
            </div>
          </div>
          {canCycle && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded border border-gray-300 text-gray-700 hover:text-blue-700 dark:border-gray-300-dark dark:text-gray-700-dark hover:dark:text-blue-700-dark"
                onClick={() => moveActive(-1)}
                aria-label="Previous disagreement forecast"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded border border-gray-300 text-gray-700 hover:text-blue-700 dark:border-gray-300-dark dark:text-gray-700-dark hover:dark:text-blue-700-dark"
                onClick={() => moveActive(1)}
                aria-label="Next disagreement forecast"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </div>
      </div>
    </SectionToggle>
  );
};

export default DisagreementForecastsChart;
